/**
 * useADSBLayer — polls the airplanes.live API for live ADS-B aircraft
 * and renders them as oriented airplane icons on a Cesium viewer.
 *
 * Polls every 5 seconds. Fetches aircraft within a radius of the camera
 * center — radius scales with zoom level.
 *
 * Color-coded by altitude band:
 *   On ground  → gray
 *   < 10,000 ft → sky blue
 *   10,000–25,000 ft → teal
 *   > 25,000 ft → purple (en-route)
 */

import { useEffect, useRef, useCallback } from 'react';
import {
  Viewer as CesiumViewer,
  CustomDataSource,
  Cartesian3,
  Cartesian2,
  Color,
  NearFarScalar,
  VerticalOrigin,
  HorizontalOrigin,
  LabelStyle,
  HeightReference,
  Math as CesiumMath,
  ConstantProperty,
} from 'cesium';
import { fetchADSBAircraft, type ADSBAircraft } from '../services/airplanesLiveApi';

// Module-level snapshot of the most recently polled aircraft, keyed by
// hex. The CesiumMap click handler reads this when an `aircraft-<hex>`
// entity is clicked so the AircraftDetailPanel popup gets full context
// without us pushing every poll into Zustand state (which would re-render
// the whole tree unnecessarily). Refreshed on every poll cycle.
const aircraftSnapshot = new Map<string, ADSBAircraft>();

export function getAircraftByHex(hex: string): ADSBAircraft | undefined {
  return aircraftSnapshot.get(hex);
}

const POLL_INTERVAL_MS = 5_000;
const DATASOURCE_NAME = 'adsb-aircraft';

// Max camera height (meters) to trigger fetch — ~600 km
const MAX_CAMERA_HEIGHT = 600_000;

// ── Airplane icon SVG ──────────────────────────────────────────────────
// Top-down silhouette in white with a subtle dark outline for readability.
// Tinted via Cesium's billboard color property.
const AIRPLANE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48">
  <g fill="white" stroke="rgba(0,0,0,0.5)" stroke-width="0.8" stroke-linejoin="round">
    <path d="M24 3 L22 14 L5 21 L5 24 L22 20.5 L21.5 34 L16 38 L16 40 L24 38 L32 40 L32 38 L26.5 34 L26 20.5 L43 24 L43 21 L26 14 Z"/>
  </g>
</svg>`;
const AIRPLANE_DATA_URI = `data:image/svg+xml;base64,${btoa(AIRPLANE_SVG)}`;

// Color by altitude band (altitudes in feet from airplanes.live)
function getAltitudeColor(altFt: number | null, onGround: boolean): Color {
  if (onGround || altFt === 0) return Color.fromCssColorString('#9ca3af'); // gray-400
  if (altFt == null) return Color.fromCssColorString('#5fd5ff');
  if (altFt < 10_000) return Color.fromCssColorString('#5fd5ff');  // sky blue (low)
  if (altFt < 25_000) return Color.fromCssColorString('#ffb547');  // teal (mid)
  return Color.fromCssColorString('#a78bfa');                       // violet (en-route)
}

// Estimate fetch radius (nm) from camera height (m)
function radiusFromHeight(heightM: number): number {
  // Rough conversion: 1 nm ≈ 1852 m, use ~half of visible extent
  const nm = (heightM / 1852) * 0.6;
  return Math.min(250, Math.max(10, Math.round(nm)));
}

/**
 * Hook export — named `useOpenSkyLayer` for backward-compat with CesiumMap.tsx
 * import but internally uses airplanes.live now.
 */
export function useOpenSkyLayer(
  viewer: CesiumViewer | null,
  visible: boolean,
) {
  const dsRef = useRef<CustomDataSource | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updateEntities = useCallback(
    async (v: CesiumViewer) => {
      try {
        const carto = v.camera.positionCartographic;
        const heightM = carto.height;

        // Skip if zoomed out too far
        if (heightM > MAX_CAMERA_HEIGHT) {
          if (dsRef.current) dsRef.current.entities.removeAll();
          return;
        }

        const centerLat = CesiumMath.toDegrees(carto.latitude);
        const centerLon = CesiumMath.toDegrees(carto.longitude);
        const radius = radiusFromHeight(heightM);

        const aircraft = await fetchADSBAircraft(centerLat, centerLon, radius);

        // Refresh the snapshot lookup so the click handler always has the
        // freshest data for any visible aircraft. Drop stale entries that
        // dropped out of this poll's radius.
        const seen = new Set<string>();
        for (const ac of aircraft) {
          aircraftSnapshot.set(ac.hex, ac);
          seen.add(ac.hex);
        }
        for (const hex of aircraftSnapshot.keys()) {
          if (!seen.has(hex)) aircraftSnapshot.delete(hex);
        }

        let ds = dsRef.current;

        if (!ds) {
          ds = new CustomDataSource(DATASOURCE_NAME);
          dsRef.current = ds;
          v.dataSources.add(ds);
        }

        // Build set of current hex IDs
        const currentIds = new Set(aircraft.map((a) => a.hex));

        // Remove stale entities
        const existing = ds.entities.values.slice();
        for (const entity of existing) {
          if (!currentIds.has(entity.id)) {
            ds.entities.removeById(entity.id);
          }
        }

        // Upsert entities
        for (const ac of aircraft) {
          const altFt = ac.altitudeBaro ?? ac.altitudeGeom ?? 0;
          const altM = altFt * 0.3048; // convert to meters for Cesium
          const color = getAltitudeColor(altFt, ac.onGround);
          const position = Cartesian3.fromDegrees(ac.longitude, ac.latitude, ac.onGround ? 0 : altM);
          // Single-line label = just the callsign (full details live in the
          // click popup). Falls back to registration → hex as needed so an
          // aircraft never shows up label-less.
          const callsign = (ac.flight?.trim() || ac.registration || ac.hex).toUpperCase();
          const rotation = ac.track != null ? -CesiumMath.toRadians(ac.track) : 0;

          // Entity IDs are prefixed `aircraft-<hex>` to match the
          // CesiumMap click handler's ID-based dispatch (track-, geofence-,
          // mission-, etc.). The hex stays unique inside the prefix.
          const entityId = `aircraft-${ac.hex}`;
          const existingEntity = ds.entities.getById(entityId);
          if (existingEntity) {
            existingEntity.position = position as any;
            if (existingEntity.billboard) {
              existingEntity.billboard.color = color as any;
              existingEntity.billboard.rotation = new ConstantProperty(rotation) as any;
            }
            if (existingEntity.label) {
              (existingEntity.label.text as any) = callsign;
            }
          } else {
            ds.entities.add({
              id: entityId,
              position,
              billboard: {
                image: AIRPLANE_DATA_URI,
                width: 34,
                height: 34,
                color,
                rotation,
                alignedAxis: Cartesian3.UNIT_Z,
                verticalOrigin: VerticalOrigin.CENTER,
                horizontalOrigin: HorizontalOrigin.CENTER,
                scaleByDistance: new NearFarScalar(5_000, 1.3, 400_000, 0.45),
                heightReference: ac.onGround ? HeightReference.CLAMP_TO_GROUND : HeightReference.NONE,
              },
              label: {
                // Single line: callsign only. Full identity / altitude /
                // speed / vertical-rate live in the popup that opens on
                // click. The label is read-at-a-glance signal; the popup
                // is read-the-detail.
                text: callsign,
                // Bake at 18px bold so the SDF rasterizer has resolution
                // headroom; scaleByDistance shrinks DOWN from there. The
                // earlier 11/12px bake was below the SDF's effective
                // resolution at 1.0 scale, producing the soft / blurry
                // look. Bigger bake + scale-down = crisp at any zoom.
                font: 'bold 18px "JetBrains Mono", ui-monospace, monospace',
                fillColor: Color.WHITE,
                style: LabelStyle.FILL,
                // No outline, no background pill — consistent with the
                // drone/pilot/sensor label treatment elsewhere on the
                // live map. Operator preference; cleaner against most
                // basemaps, can wash out on bright satellite imagery.
                showBackground: false,
                pixelOffset: new Cartesian2(0, -24),
                verticalOrigin: VerticalOrigin.BOTTOM,
                horizontalOrigin: HorizontalOrigin.CENTER,
                // Cap near scale at 0.85 so we always render slightly
                // smaller than the bake, keeping SDF crisp.
                scaleByDistance: new NearFarScalar(5_000, 0.85, 400_000, 0.4),
                translucencyByDistance: new NearFarScalar(50_000, 1, 500_000, 0),
              },
            });
          }
        }
      } catch (err) {
        console.warn('[ADS-B] Fetch error:', err);
      }
    },
    [],
  );

  useEffect(() => {
    if (!viewer || viewer.isDestroyed()) return;

    if (!visible) {
      if (dsRef.current) dsRef.current.show = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (dsRef.current) dsRef.current.show = true;

    // Initial fetch
    updateEntities(viewer);

    // Start polling
    intervalRef.current = setInterval(() => {
      if (!viewer.isDestroyed()) updateEntities(viewer);
    }, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [viewer, visible, updateEntities]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dsRef.current && viewer && !viewer.isDestroyed()) {
        viewer.dataSources.remove(dsRef.current, true);
        dsRef.current = null;
      }
    };
  }, [viewer]);
}
